import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import oWnApi from 'utils/WnApi';
import * as CheckoutActions from 'components/Checkout/redux/actions';
import useIsUserAuthenticated from 'utils/hooks/useIsUserAuthenticated';
import { selectAllEventsInCart } from '../redux/selectors';
import { formatRegistrationDataForConfirmationEmail } from './helpers';

interface Props {
  fnSetIsLoading: Function;
}

const useFinalizePaidEventRegistration = ({ fnSetIsLoading }: Props) => {
  const fnDispatch = useDispatch();
  const bIsUserAuthenticated = useIsUserAuthenticated();
  const oCheckout = useSelector((oState: any) => oState.Checkout);
  const [oSearchParams] = useSearchParams();
  const iTotalCost = useSelector(
    (oState: any) => oState.Checkout.checkoutTotal
  );
  const aEventsInCart = useSelector(selectAllEventsInCart);
  // Flag to avoid sending multiple PATCH API Calls to finalize registration.
  const hasPatchedRef = useRef(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (iTotalCost === 0) {
      hasPatchedRef.current = true; // Free registrations are already complete.
      fnSetIsLoading(false);
    }
  }, [iTotalCost, fnSetIsLoading]);

  useEffect(() => {
    const finalizePaidEventRegistration = async () => {
      try {
        let sTransactionTotal = oSearchParams.get('transaction_total') ?? '';
        sTransactionTotal = sTransactionTotal.replace(/,/g, '');
        const iPaymentTransactionTotal = parseFloat(sTransactionTotal);
        const sPaymentTransactionId = oSearchParams.get('transid');
        const sEncodedDadTransactionId =
          oSearchParams.get('dad_transaction_id');

        // Calculate subtotal and previously paid using logic from EventEditConfirmationFooter
        const aEditedEventsInCart = aEventsInCart.filter(
          (oEvent: any) => oEvent.sType === 'event-edit'
        );
        const iTotalPreviouslyPaid = aEditedEventsInCart.reduce(
          (iTotal: number, oEventInCart: any) => {
            const { aGuests } = oEventInCart.oDetails;
            const { TOTAL_AMOUNT_PAID = 0 } =
              aGuests.find((oGuest: any) => oGuest.ROLE === 'currentHost') ??
              {};
            return iTotal + TOTAL_AMOUNT_PAID;
          },
          0
        );
        const iSubTotal = aEditedEventsInCart.reduce(
          (iTotal: number, oEventInCart: any) => {
            const { aGuests } = oEventInCart.oDetails;
            const iGuestTotal = aGuests.reduce(
              (iSum: number, oGuest: any) =>
                iSum + (oGuest.REG_OPTION.COST ?? 0),
              0
            );
            return iTotal + iGuestTotal;
          },
          0
        );

        const oRegistrationDataForConfirmationEmail =
          formatRegistrationDataForConfirmationEmail(
            iTotalCost,
            aEventsInCart,
            oCheckout
          );

        hasPatchedRef.current = true;

        const oResponse = await oWnApi.patch(
          'events/registrations',
          {
            ...oRegistrationDataForConfirmationEmail,
            paymentTransactionId: sPaymentTransactionId,
            dadTransactionId: sEncodedDadTransactionId,
            paymentAmount: iPaymentTransactionTotal,
            subtotalAmount: iSubTotal,
            previouslyPaidAmount: iTotalPreviouslyPaid,
          },
          bIsUserAuthenticated
        );

        if (oResponse.status === 200) {
          if (oResponse.data.maskedConfirmationEmail) {
            fnDispatch(
              CheckoutActions.update({
                email: oResponse.data.maskedConfirmationEmail,
              })
            );
          }
        } else {
          throw new Error(
            `Failed to finalize paid event registration. ${oResponse.status}: ${oResponse.data}`
          );
        }
      } catch (oError) {
        console.error(oError);
        setError(oError as Error);
      } finally {
        fnSetIsLoading(false);
      }
    };

    if (
      !hasPatchedRef.current && // Only attempt to finalize once
      iTotalCost > 0 && // Only finalize if there is a cost
      bIsUserAuthenticated !== null // Ensure we know if the user is authenticated
    ) {
      finalizePaidEventRegistration();
    }
  }, [
    iTotalCost,
    oSearchParams,
    bIsUserAuthenticated,
    fnDispatch,
    aEventsInCart,
    fnSetIsLoading,
    oCheckout,
  ]);

  if (error) {
    throw error; // Let the error boundary handle the error
  }
};

export default useFinalizePaidEventRegistration;
